// src/pages/AdvancedPage.js
import React, { useState, useEffect, useContext } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";

import "./BasicPage.css";

// Context for the Context API example
const MyContext = React.createContext();

function ThemedButton() {
  const value = useContext(MyContext);
  return <button className="theme-button">{value}</button>;
}

function Toolbar() {
  return (
    <div>
      <ThemedButton />
    </div>
  );
}

function AdvancedPage() {
  const [count, setCount] = useState(0);

  const Code1 = `import React, { useState } from 'react';

function Example() {
  const [count, setCount] = useState(0);

  return (
    <div>
      <p>You clicked {count} times</p>
      <button onClick={() => setCount(count + 1)}>
        Click me
      </button>
    </div>
  );
}`;

  const Code2 = `import React, { useEffect, useState } from 'react';

function Example() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    document.title = \`You clicked \${count} times\`;
  });

  return (
    <div>
      <p>You clicked {count} times</p>
      <button onClick={() => setCount(count + 1)}>
        Click me
      </button>
    </div>
  );
}`;

  const Code3 = `const MyContext = React.createContext();

function App() {
  return (
    <MyContext.Provider value="Hello from Context">
      <Toolbar />
    </MyContext.Provider>
  );
}

function Toolbar() {
  return (
    <div>
      <ThemedButton />
    </div>
  );
}

function ThemedButton() {
  return (
    <MyContext.Consumer>
      {value => <Button theme={value} />}
    </MyContext.Consumer>
  );
}`;

  const Code4 = `import { BrowserRouter as Router, Route, Link } from 'react-router-dom';

function App() {
  return (
    <Router>
      <div>
        <nav>
          <ul>
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/about">About</Link>
            </li>
          </ul>
        </nav>

        <Route path="/" exact component={Home} />
        <Route path="/about" component={About} />
      </div>
    </Router>
  );
}

function Home() {
  return <h2>Home</h2>;
}

function About() {
  return <h2>About</h2>;
}`;

  const Code5 = `import { createStore } from 'redux';
import { Provider, connect } from 'react-redux';

// Actions
const increment = () => ({ type: 'INCREMENT' });
const decrement = () => ({ type: 'DECREMENT' });

// Reducer
const counter = (state = 0, action) => {
  switch (action.type) {
    case 'INCREMENT':
      return state + 1;
    case 'DECREMENT':
      return state - 1;
    default:
      return state;
  }
};

// Store
let store = createStore(counter);

// Component
function Counter({ value, increment, decrement }) {
  return (
    <div>
      <h1>{value}</h1>
      <button onClick={increment}>+</button>
      <button onClick={decrement}>-</button>
    </div>
  );
}

// Mapping state and dispatch to props
const mapStateToProps = state => ({ value: state });
const mapDispatchToProps = { increment, decrement };

const ConnectedCounter = connect(mapStateToProps, mapDispatchToProps)(Counter);

// App
function App() {
  return (
    <Provider store={store}>
      <ConnectedCounter />
    </Provider>
  );
}`;

  return (
    <div className="BasicApp">
      <div className="BasicPage m-2">
        <h2 className="text-3xl">Advanced Concepts 📚</h2>
        <hr />
      </div>
      <div className="Notes-Container m-4">
        <h3 className="text-xl mt-8 mb-4">Hooks 🎣</h3>
        <hr className="w-[40%] mb-2" />
        <p>
          Hooks allow you to use state and other React features without writing
          a class.
          <br />
          &gt;They are functions that let you "hook into" React state and
          lifecycle features from function components.
          <br />
          &gt;The useState hook allows you to add state to functional
          components, <br />
          &gt;The useEffect hook enables you to perform side effects in
          functional components.
        </p>
        <h4 className="m-2 text-lg">useState:</h4>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code1}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <div className="m-4">
          <p>You clicked {count} times</p>
          <button
            onClick={() => setCount(count + 1)}
            type="button"
            className="m-2 py-2.5 px-5 me-2 mb-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-full border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
          >
            Click me
          </button>
        </div>

        <h4 className="m-2 text-lg">useEffect:</h4>
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code2}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-4 font-bold">Explanation:</h4>
        <p className="m-4">
          The useState example shows how to manage state within a functional
          component. The useEffect example demonstrates how to perform side
          effects in a functional component, such as updating the document
          title.
        </p>

        <h3 className="text-xl mt-8 mb-4">Context API 🌐</h3>
        <hr className="w-[40%] mb-2" />
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code3}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <MyContext.Provider value="Hello from Context">
          <Toolbar />
        </MyContext.Provider>
        <h4 className="m-4 font-bold">Explanation:</h4>
        <p className="m-4">
          The Context API allows you to pass data through the component tree
          without having to pass props manually at every level. The MyContext
          object holds the context, and the Provider component supplies the
          context value. The Consumer component or useContext hook accesses the
          context value.
        </p>

        <h3 className="text-xl mt-8 mb-4">React Router 🚦</h3>
        <hr className="w-[40%] mb-2" />
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code4}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-4 font-bold">Explanation:</h4>
        <p className="m-4">
          React Router allows for navigation between different components in a
          React application. The Router component wraps the application, and
          Route components define the paths and the corresponding components to
          render. The Link component enables navigation between routes.
        </p>

        <h3 className="text-xl mt-8 mb-4">State Management (Redux) 🗂️</h3>
        <hr className="w-[40%] mb-2" />
        <div className="Code-Editor-Container">
          <CodeMirror
            value={Code5}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <h4 className="m-4 font-bold">Explanation:</h4>
        <p className="m-4">
          Redux is a state management library for managing and centralizing
          application state. It consists of a store, reducers, and actions. The
          Provider component supplies the store to the application, and the
          connect function connects React components to the Redux store.
        </p>

        <h3 className="text-xl mt-8 mb-4">
          Building and Deploying a React App 🚀
        </h3>
        <hr className="w-[40%] mb-2" />
        <p>
          To build and deploy a React application, you first create an optimized
          production build using `npm run build`. You can then deploy the build
          to various hosting services such as Netlify, Vercel, GitHub Pages, or
          any web server. For instance, to deploy to GitHub Pages:
        </p>
        <div className="Code-Editor-Container flex-row">
          {/* Explain in brief this coomannd npm install gh-pages --save-dev */}
          <p className="mt-4">1. Install the gh-pages package as a dev dependency:</p>

          <CodeMirror
            value={`npm install gh-pages --save-dev`}
            extensions={[]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />

          <p className="mt-4">2. Add the following scripts to your package.json file:</p>
          <CodeMirror
            value={`// package.json scripts
            "scripts": {
            "predeploy": "npm run build",
            "deploy": "gh-pages -d build"
            }
            `}
            extensions={[javascript({ jsx: true })]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />

          <p className="mt-4">
            3. Deploy the application to GitHub Pages using the following command:
          </p>

          <CodeMirror
            value={`npm run deploy`}
            extensions={[]}
            theme={vscodeDark}
            height="100%"
            readOnly={true}
          />
        </div>
        <p className="mt-12 text-xl">
          Congratulations! You have completed the React Frontend Development
          Roadmap. Happy coding!
        </p>
      </div>
    </div>
  );
}

export default AdvancedPage;
